import { Input, Label, YStack } from 'tamagui';
import { retainSubdivision, subdivisionsFor } from '@cartrack/shared';

export interface LocationValue {
  country: string;
  stateProvince: string | null | undefined;
  postalCode: string | null | undefined;
}

export interface LocationFieldsProps {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  labelPrefix?: string;
}

const COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'Korea, South' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'AE', name: 'United Arab Emirates' },
];

/**
 * Mirrors the API's `@MaxLength(10)` on `stateProvince` (user.controller.ts) and
 * `garageStateProvince` (vehicle/dto.ts). The web and API caps must stay in sync —
 * widening this requires changing both API DTOs and the spec together, not just here.
 */
const STATE_PROVINCE_TEXT_MAX_LENGTH = 10;

const postalLabelByCountry: Record<string, string> = {
  US: 'ZIP',
  CA: 'Postal code',
  GB: 'Postcode',
  AU: 'Postcode',
  NZ: 'Postcode',
};

const stateLabelByCountry: Record<string, string> = {
  US: 'State',
  CA: 'Province',
  MX: 'State',
  AU: 'State/Territory',
  GB: 'County',
  IE: 'County',
};

/**
 * The state/province control: a dropdown for countries whose subdivisions we curate,
 * free text everywhere else. Split out of LocationFields so each component keeps one
 * clear job — inline, the two branches pushed LocationFields over the CRAP fail line.
 */
function StateProvinceField({
  country,
  stateProvince,
  onSelect,
}: {
  country: string;
  stateProvince: string | null | undefined;
  onSelect: (next: string | null) => void;
}): JSX.Element {
  const subdivisions = subdivisionsFor(country);
  const current = stateProvince ?? '';

  if (!subdivisions) {
    return (
      <Input
        id="location-state-province"
        value={current}
        onChangeText={(t) => onSelect(t || null)}
        placeholder="Region"
        maxLength={STATE_PROVINCE_TEXT_MAX_LENGTH}
      />
    );
  }

  return (
    <select
      id="location-state-province"
      className="native-control"
      value={current}
      onChange={(e) => onSelect(e.target.value || null)}
    >
      <option value="">Select…</option>
      {subdivisions.map((s) => (
        <option key={s.code} value={s.code}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

export function LocationFields({
  value,
  onChange,
  labelPrefix,
}: LocationFieldsProps): JSX.Element {
  const countryLabel = labelPrefix ? `${labelPrefix} country` : 'Country';
  const stateLabel =
    (labelPrefix ? `${labelPrefix} ` : '') +
    (stateLabelByCountry[value.country] ?? 'State/Province').toLowerCase();
  const postalLabel =
    (labelPrefix ? `${labelPrefix} ` : '') +
    (postalLabelByCountry[value.country] ?? 'Postal code').toLowerCase();

  return (
    <YStack gap="$2">
      <Label htmlFor="location-country">{countryLabel}</Label>
      <select
        id="location-country"
        className="native-control"
        value={value.country}
        onChange={(e) =>
          onChange({
            ...value,
            country: e.target.value,
            stateProvince: retainSubdivision(e.target.value, value.stateProvince),
          })
        }
      >
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>

      <Label htmlFor="location-state-province">{capitalize(stateLabel)}</Label>
      <StateProvinceField
        country={value.country}
        stateProvince={value.stateProvince}
        onSelect={(next) => onChange({ ...value, stateProvince: next })}
      />

      <Label htmlFor="location-postal-code">{capitalize(postalLabel)}</Label>
      <Input
        id="location-postal-code"
        value={value.postalCode ?? ''}
        onChangeText={(t) => onChange({ ...value, postalCode: t || null })}
        placeholder={postalPlaceholder(value.country)}
        maxLength={16}
      />
    </YStack>
  );
}

function capitalize(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

function postalPlaceholder(country: string): string {
  switch (country) {
    case 'US':
      return '94103';
    case 'CA':
      return 'M5V 3L9';
    case 'GB':
      return 'SW1A 1AA';
    case 'AU':
      return '2000';
    case 'DE':
      return '10115';
    default:
      return '';
  }
}
